import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { startCheckout, openLunipay, claimLunipay } from "@/lib/config";
import PayPalCheckoutButton from "@/components/PayPalCheckoutButton";
import { X, Send, Calendar, Loader2, CheckCircle2, ChevronLeft, ChevronRight, Lock, ExternalLink, Shield } from "lucide-react";
import { WORKSHOP_TITLE, WORKSHOP_HEADLINE, WORKSHOP_DESC } from "@/lib/constants";

const WEEKDAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function visitorTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
}

function fmtDayKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function BookingModal({ assessmentId, open, onClose, defaultEmail = "", access = null, onAccessRefresh }) {
  const [phase, setPhase] = useState("loading"); // loading | not_configured | advisory_paywall | picking | confirming | sending | done
  const [advisoryCheckingOut, setAdvisoryCheckingOut] = useState(false);
  const [lunipayOpened, setLunipayOpened] = useState(false);
  const [advProvider, setAdvProvider] = useState("lunipay");
  const [slots, setSlots] = useState([]);
  const [hostTz, setHostTz] = useState("Europe/London");
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState(defaultEmail);
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // Esc closes the modal — matches the established modal UX in the app.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);
  const tz = useMemo(() => visitorTz(), []);
  const processorName = access?.processor_name || "Payment Processor";

  useEffect(() => {
    if (!open) return;
    setPhase("loading");
    setSelectedSlot(null);
    setConfirmation(null);
    setLunipayOpened(false);
    setAdvProvider(access?.paypal_enabled ? "paypal" : "lunipay");
    (async () => {
      try {
        // Paywall gate: if active + advisory not paid, show paywall screen instead of slots.
        if (access?.paywall_active && !access?.paid_advisory) {
          setPhase("advisory_paywall");
          return;
        }
        const [cfg, sl] = await Promise.all([
          api.get("/booking/config"),
          api.get("/booking/slots"),
        ]);
        setSlots(sl.data.slots || []);
        setHostTz(sl.data.timezone || cfg.data.timezone || "Europe/London");
        if (!cfg.data.configured || !sl.data.slots?.length) {
          setPhase("not_configured");
        } else {
          const firstDay = new Date(sl.data.slots[0].start_iso);
          setSelectedDay(fmtDayKey(firstDay));
          setPhase("picking");
        }
      } catch (e) {
        console.error(e);
        toast.error("Could not load booking options.");
        setPhase("not_configured");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, access?.paywall_active, access?.paid_advisory]);

  const startAdvisoryCheckout = async () => {
    if (!assessmentId) {
      toast.error("Open this from your assessment summary first.");
      return;
    }
    // LuniPay flat-fee path (Iteration 19): open the consultation product
    // page in a new tab. The user clicks "I've paid" when they return.
    if (access?.payment_processor === "lunipay") {
      const popup = openLunipay(access.lunipay_advisory_url);
      if (!popup) {
        toast.error("Your browser blocked the popup. Please allow popups for this site, then try again.");
        return;
      }
      setLunipayOpened(true);
      toast.message(`${processorName} opened in a new tab. Come back here after you've paid.`);
      return;
    }
    // Stripe (mothballed) — kept for one-line reinstatement.
    try {
      setAdvisoryCheckingOut(true);
      await startCheckout({ assessment_id: assessmentId, tier: "advisory" });
    } catch (e) {
      console.error(e);
      toast.error("Could not start checkout. Please try again.");
      setAdvisoryCheckingOut(false);
    }
  };

  const loadSlotsNow = async () => {
    try {
      const [cfg, sl] = await Promise.all([
        api.get("/booking/config"),
        api.get("/booking/slots"),
      ]);
      setSlots(sl.data.slots || []);
      setHostTz(sl.data.timezone || cfg.data.timezone || "Europe/London");
      if (!cfg.data.configured || !sl.data.slots?.length) {
        setPhase("not_configured");
      } else {
        const firstDay = new Date(sl.data.slots[0].start_iso);
        setSelectedDay(fmtDayKey(firstDay));
        setPhase("picking");
      }
    } catch (e) {
      console.error(e);
      toast.error("Unlocked, but couldn't load slots. Please reopen this modal.");
      setPhase("not_configured");
    }
  };

  // PayPal capture succeeded for the advisory tier → refresh access + show slots.
  const onAdvisoryPaidViaPaypal = async () => {
    onAccessRefresh?.();
    setPhase("loading");
    await loadSlotsNow();
  };

  const claimAdvisoryUnlock = async () => {
    if (!assessmentId) return;
    try {
      setAdvisoryCheckingOut(true);
      const res = await claimLunipay({ assessment_id: assessmentId, tier: "advisory" });
      if (res.already_paid) {
        toast.message("Workshop already unlocked. Loading slots…");
      } else {
        toast.success("Workshop unlocked! Pick your slot below.");
      }
      onAccessRefresh?.();
      setPhase("loading");
      setLunipayOpened(false);
      await loadSlotsNow();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Could not unlock. Please try again.");
    } finally {
      setAdvisoryCheckingOut(false);
    }
  };

  // Group slots by visitor-local day
  const slotsByDay = useMemo(() => {
    const groups = {};
    for (const s of slots) {
      const d = new Date(s.start_iso);
      const key = fmtDayKey(d);
      (groups[key] ||= []).push(s);
    }
    return groups;
  }, [slots]);

  const dayKeys = useMemo(() => Object.keys(slotsByDay).sort(), [slotsByDay]);
  const daysInWindow = useMemo(() => dayKeys.map((k) => {
    const [y, m, day] = k.split("-").map((n) => parseInt(n, 10));
    return { key: k, date: new Date(y, m - 1, day) };
  }), [dayKeys]);

  const currentDayIdx = selectedDay ? dayKeys.indexOf(selectedDay) : -1;
  const visibleSlots = selectedDay ? slotsByDay[selectedDay] || [] : [];

  const goPrevDay = () => { if (currentDayIdx > 0) setSelectedDay(dayKeys[currentDayIdx - 1]); };
  const goNextDay = () => { if (currentDayIdx >= 0 && currentDayIdx < dayKeys.length - 1) setSelectedDay(dayKeys[currentDayIdx + 1]); };

  const submit = async () => {
    if (!selectedSlot) return toast.error("Please pick a time first.");
    if (!name.trim()) return toast.error("Please share your name.");
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Please enter a valid email.");
    if (!consent) return toast.error("Please tick the consent box to submit.");

    try {
      setPhase("sending");
      const { data } = await api.post("/booking/request", {
        assessment_id: assessmentId || null,
        slot_start_iso: selectedSlot.start_iso,
        name: name.trim(),
        email: email.trim(),
        message: message.trim() || null,
        consent: true,
      });
      setConfirmation(data);
      setPhase("done");
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail || "Could not complete booking. Please try again.";
      toast.error(detail);
      setPhase("picking");
    }
  };

  const formatSlotLocal = (iso) => {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center px-4 py-6 overflow-y-auto" onClick={onClose}>
      <div
        role="dialog"
        aria-label="Book a workshop"
        className="w-full max-w-xl rounded-xl bg-white dark:bg-slate-900 shadow-xl border border-slate-200 dark:border-slate-800"
        onClick={(e) => e.stopPropagation()}
        data-testid="booking-modal"
      >
        <div className="flex items-center justify-between px-6 pt-5">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-teal-600 dark:text-teal-400" />
            <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Book a workshop</p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="booking-close" className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 pt-2 pb-6 space-y-4">
          <div>
            <h3 className="font-display text-xl font-semibold text-slate-900 dark:text-slate-50 tracking-tight">
              Pick a time that works
            </h3>
            <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              Times shown in your timezone (<b>{tz}</b>). The host is in <b>{hostTz}</b>. A calendar invite is emailed to you on confirmation.
            </p>
          </div>

          {phase === "loading" && (
            <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading availability…
            </div>
          )}

          {phase === "not_configured" && (
            <div data-testid="booking-not-configured" className="rounded-lg border border-amber-300 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-500/10 p-4">
              <p className="text-sm text-slate-800 dark:text-slate-100">
                Bookings are not yet open. Please check back soon, or email the team directly.
              </p>
            </div>
          )}

          {phase === "advisory_paywall" && (
            <div data-testid="advisory-paywall" className="rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40 p-5">
              <p className="text-[11px] uppercase tracking-[0.14em] text-teal-700 dark:text-teal-400">{WORKSHOP_TITLE}</p>
              <h4 className="mt-2 font-display text-lg font-semibold text-slate-900 dark:text-slate-50">
                {WORKSHOP_HEADLINE}
              </h4>
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                {WORKSHOP_DESC}
              </p>
              <div className="mt-5 flex items-baseline justify-between gap-3">
                <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                  {access?.flat_advisory_label || access?.advisory_label || WORKSHOP_TITLE}
                </p>
                <p className="font-display text-3xl font-semibold text-slate-900 dark:text-slate-50">
                  {typeof access?.flat_advisory_price_usd === "number" ? `$${access.flat_advisory_price_usd.toFixed(0)}` : "—"}
                  <span className="text-xs text-slate-500 dark:text-slate-400 font-normal ml-1">USD</span>
                </p>
              </div>

              {access?.paypal_enabled && access?.payment_processor === "lunipay" && access?.lunipay_claim_enabled && (
                <div className="mt-5" data-testid="advisory-provider-picker">
                  <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-2">Choose how to pay</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setAdvProvider("paypal")}
                      data-testid="advisory-pick-paypal"
                      className={`h-11 rounded-md border text-sm font-medium transition-colors ${
                        advProvider === "paypal"
                          ? "border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200"
                          : "border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`}
                    >
                      PayPal / Card
                    </button>
                    <button
                      type="button"
                      onClick={() => setAdvProvider("lunipay")}
                      data-testid="advisory-pick-lunipay"
                      className={`h-11 rounded-md border text-sm font-medium transition-colors ${
                        advProvider === "lunipay"
                          ? "border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200"
                          : "border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                      }`}
                    >
                      {processorName}
                    </button>
                  </div>
                </div>
              )}

              {(advProvider === "paypal" && access?.paypal_enabled) ? (
                <div className="mt-5 space-y-3">
                  <PayPalCheckoutButton
                    clientId={access.paypal_client_id}
                    productSlug="advisory"
                    resourceRef={assessmentId}
                    onPaid={onAdvisoryPaidViaPaypal}
                  />
                  <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                    <Shield className="h-3 w-3" /> Secure payment via PayPal — pay with PayPal or any card
                  </div>
                </div>
              ) : access?.payment_processor === "lunipay" && access?.lunipay_claim_enabled && lunipayOpened ? (
                <div className="mt-5 space-y-3">
                  <div className="rounded-md border border-teal-200 dark:border-teal-900/60 bg-teal-50/80 dark:bg-teal-900/20 p-4 text-sm text-teal-900 dark:text-teal-100">
                    <p className="font-semibold flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4" /> {processorName} opened in a new tab.
                    </p>
                    <p className="mt-1.5 text-[13px] leading-relaxed">
                      Complete your ${(access?.flat_advisory_price_usd ?? 0).toFixed?.(0) || "—"} payment there. Once you see the confirmation, come back here and click the button below — your scheduler unlocks instantly.
                    </p>
                  </div>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <button
                      onClick={() => openLunipay(access.lunipay_advisory_url)}
                      data-testid="advisory-lunipay-reopen-btn"
                      type="button"
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      <ExternalLink className="h-3 w-3" /> Reopen {processorName}
                    </button>
                    <button
                      onClick={claimAdvisoryUnlock}
                      disabled={advisoryCheckingOut}
                      data-testid="advisory-lunipay-claim-btn"
                      className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-500 transition-colors disabled:opacity-50"
                    >
                      {advisoryCheckingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      {advisoryCheckingOut ? "Unlocking…" : "I've paid — pick my slot"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    onClick={startAdvisoryCheckout}
                    disabled={advisoryCheckingOut}
                    data-testid="advisory-checkout-btn"
                    className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors disabled:opacity-50 w-full justify-center"
                  >
                    {advisoryCheckingOut ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                    {advisoryCheckingOut
                      ? "Opening…"
                      : access?.payment_processor === "lunipay"
                        ? `Pay $${(access?.flat_advisory_price_usd ?? 0).toFixed?.(0) || "—"} on ${processorName}`
                        : `Pay $${(access?.flat_advisory_price_usd ?? access?.advisory_price_usd)?.toFixed?.(0) || "—"} & pick a time`}
                    {access?.payment_processor === "lunipay" && !advisoryCheckingOut && <ExternalLink className="h-4 w-4" />}
                  </button>
                  <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400 text-center">
                    {access?.payment_processor === "lunipay"
                      ? `Secure payment via ${processorName} · After payment, return here and click 'I've paid' to unlock the scheduler`
                      : "Secure checkout via Stripe · After payment you'll be brought back to pick a slot"}
                  </p>
                </>
              )}
            </div>
          )}

          {(phase === "picking" || phase === "sending") && (
            <>
              {/* Day picker */}
              <div className="flex items-center gap-2 overflow-x-auto pb-1">
                <button
                  onClick={goPrevDay}
                  disabled={currentDayIdx <= 0}
                  data-testid="booking-prev-day"
                  className="h-9 w-9 shrink-0 rounded-md border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-700 dark:text-slate-200 disabled:opacity-30"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <div className="flex items-center gap-2 overflow-x-auto">
                  {daysInWindow.map(({ key, date }) => (
                    <button
                      key={key}
                      onClick={() => { setSelectedDay(key); setSelectedSlot(null); }}
                      data-testid={`booking-day-${key}`}
                      className={`shrink-0 h-12 px-3 rounded-md border text-center transition-colors ${
                        selectedDay === key
                          ? "border-slate-900 dark:border-teal-500 bg-slate-900 dark:bg-teal-600 text-white"
                          : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-100"
                      }`}
                    >
                      <p className="text-[10px] uppercase tracking-wider opacity-80">{WEEKDAY_SHORT[(date.getDay() + 6) % 7]}</p>
                      <p className="text-sm font-medium leading-tight">{date.getDate()} {MONTH_SHORT[date.getMonth()]}</p>
                    </button>
                  ))}
                </div>
                <button
                  onClick={goNextDay}
                  disabled={currentDayIdx === -1 || currentDayIdx >= dayKeys.length - 1}
                  data-testid="booking-next-day"
                  className="h-9 w-9 shrink-0 rounded-md border border-slate-200 dark:border-slate-700 flex items-center justify-center text-slate-700 dark:text-slate-200 disabled:opacity-30"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>

              {/* Slot grid */}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {visibleSlots.map((s) => {
                  const active = selectedSlot?.start_iso === s.start_iso;
                  return (
                    <button
                      key={s.start_iso}
                      onClick={() => setSelectedSlot(s)}
                      data-testid={`booking-slot-${s.start_iso}`}
                      className={`h-10 rounded-md border text-sm font-medium transition-colors ${
                        active
                          ? "border-slate-900 dark:border-teal-500 bg-slate-900 dark:bg-teal-600 text-white"
                          : "border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-100"
                      }`}
                    >
                      {formatSlotLocal(s.start_iso)}
                    </button>
                  );
                })}
                {visibleSlots.length === 0 && (
                  <p className="col-span-full text-xs text-slate-500 dark:text-slate-400">No times available this day — try another.</p>
                )}
              </div>

              {/* Contact form */}
              <div className="mt-2 space-y-3 pt-4 border-t border-slate-200 dark:border-slate-800">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Your name</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                    data-testid="booking-name"
                    className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Your email</label>
                  <input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    data-testid="booking-email"
                    className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">What would help most? (optional)</label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={3}
                    placeholder="A short note — priorities, timing, constraints."
                    data-testid="booking-message"
                    className="mt-1.5 w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-600"
                  />
                </div>
                <label className="flex items-start gap-2.5 text-xs text-slate-600 dark:text-slate-300 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="booking-consent"
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span>
                    I agree to share my booking details with SmartNote2Self for the purpose of this call.
                    I understand a calendar invite will be emailed to me.
                  </span>
                </label>

                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    onClick={onClose}
                    className="h-10 px-4 rounded-md border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submit}
                    disabled={phase === "sending"}
                    data-testid="booking-submit"
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-slate-900 dark:bg-teal-600 text-white text-sm font-medium hover:bg-slate-800 dark:hover:bg-teal-500 transition-colors disabled:opacity-50"
                  >
                    {phase === "sending" ? <><Loader2 className="h-4 w-4 animate-spin" /> Booking…</> : <>Confirm booking <Send className="h-4 w-4" /></>}
                  </button>
                </div>
              </div>
            </>
          )}

          {phase === "done" && confirmation && (
            <div data-testid="booking-done" className="rounded-lg border border-teal-300 dark:border-teal-500/40 bg-teal-50 dark:bg-teal-500/10 p-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-teal-700 dark:text-teal-300 mt-0.5" />
                <div>
                  <p className="font-display text-base font-semibold text-slate-900 dark:text-slate-50">
                    Booked — see you {confirmation.slot_start_iso ? new Date(confirmation.slot_start_iso).toLocaleString(undefined, { weekday: "long", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : confirmation.slot_local} <span className="text-slate-500 dark:text-slate-400 font-normal">({tz})</span>
                  </p>
                  <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
                    That's <b>{confirmation.slot_local}</b> in the host's timezone.
                  </p>
                  <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">
                    A calendar invite has been emailed to <b>{email}</b>. You'll also hear from us directly if anything needs to change.
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="mt-4 h-10 px-4 rounded-md border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
